/**
 * monitoring.js — Sentry crash reporting.
 *
 * Init is a no-op unless REACT_APP_SENTRY_DSN is set at build time.
 * The Capacitor SDK auto-detects the platform and captures both
 * JS-side errors and native crashes (via the iOS/Android SDKs under
 * the hood). On web it degrades gracefully to the JS-only path.
 *
 * Note: @sentry/react is intentionally NOT installed. @sentry/capacitor
 * pins it to a specific version and npm peer-dep errors when a mismatch
 * is present. Plain @sentry/capacitor captures all JS errors via its
 * bundled @sentry/browser. Add @sentry/react only if you need the
 * ErrorBoundary / Profiler components, and pin it to the exact version
 * @sentry/capacitor requires.
 */
import * as Sentry from '@sentry/capacitor';

let _initialized = false;

export function initSentry() {
  if (_initialized) return;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;
  try {
    Sentry.init({
      dsn,
      environment: process.env.REACT_APP_ENV || process.env.NODE_ENV || 'production',
      release: process.env.REACT_APP_RELEASE || undefined,
      tracesSampleRate: 0.1,
      /* Don't send default PII — the app handles sensitive data. */
      sendDefaultPii: false,
    });
    _initialized = true;
  } catch (e) {
    console.warn('[monitoring] Sentry init failed:', e);
  }
}

/**
 * Attach a user context after login. Strip PII beyond ID + workspace.
 */
export function setMonitoringUser(user) {
  if (!_initialized) return;
  try {
    Sentry.setUser(user ? { id: user.id, wsKey: user.wsKey, role: user.role } : null);
  } catch {}
}

export function captureError(err, context) {
  if (!_initialized) {
    console.error('[monitoring]', err, context);
    return;
  }
  try {
    Sentry.captureException(err, context ? { extra: context } : undefined);
  } catch {}
}
